import React from 'react';
import {
  Box,
  Container,
  Typography,
  Stack,
} from '@mui/material';
import { styled, keyframes } from '@mui/system';
import CelebrationRoundedIcon from '@mui/icons-material/CelebrationRounded';
import HowToVoteRoundedIcon from '@mui/icons-material/HowToVoteRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import InstagramAd from '../audiencecomps/nongame_views/InstagramAd';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const driftGlow = keyframes`
  0%, 100% {
    transform: translateX(-6%) translateY(0%);
    opacity: 0.58;
  }
  50% {
    transform: translateX(6%) translateY(-2%);
    opacity: 0.85;
  }
`;

const AnimatedContainer = styled(Container)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(4, 1.5, 7),
  animation: `${fadeIn} 520ms ease-out`,
}));

const GlassCard = ({ children, sx = {} }) => (
  <Box
    sx={{
      position: 'relative',
      overflow: 'hidden',
      borderRadius: '30px',
      background:
        'linear-gradient(180deg, rgba(29,22,39,0.97) 0%, rgba(20,23,31,0.98) 42%, rgba(18,21,27,0.99) 100%)',
      border: '1px solid rgba(255,255,255,0.08)',
      boxShadow: `
        0 18px 42px rgba(0,0,0,0.34),
        0 0 42px rgba(210,110,255,0.12),
        inset 0 1px 0 rgba(255,255,255,0.06)
      `,
      px: { xs: 2.25, sm: 3.5 },
      py: { xs: 3, sm: 3.75 },
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        background:
          'radial-gradient(circle at 50% 0%, rgba(210,110,255,0.20), transparent 46%)',
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        left: 24,
        right: 24,
        top: 0,
        height: '2px',
        background:
          'linear-gradient(90deg, transparent, rgba(225,160,255,0.85), rgba(255,255,255,0.92), rgba(225,160,255,0.85), transparent)',
        opacity: 0.9,
      },
      ...sx,
    }}
  >
    <Box sx={{ position: 'relative', zIndex: 1 }}>
      {children}
    </Box>
  </Box>
);

const FeaturePill = ({ icon, title, text }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: '22px',
      background: 'rgba(255,255,255,0.045)',
      border: '1px solid rgba(255,255,255,0.08)',
      textAlign: 'left',
      height: '100%',
    }}
  >
    <Stack direction="row" spacing={1.2} alignItems="center" sx={{ mb: 1 }}>
      <Box
        sx={{
          width: 38,
          height: 38,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          background:
            'radial-gradient(circle at 35% 30%, rgba(255,255,255,0.18), rgba(205,120,255,0.18) 42%, rgba(135,72,230,0.08) 100%)',
          border: '1px solid rgba(255,255,255,0.09)',
        }}
      >
        {icon}
      </Box>

      <Typography
        sx={{
          fontWeight: 900,
          color: 'white',
          fontSize: '1.02rem',
          letterSpacing: '-0.02em',
        }}
      >
        {title}
      </Typography>
    </Stack>

    <Typography
      sx={{
        color: 'rgba(255,255,255,0.68)',
        lineHeight: 1.38,
        fontSize: '0.95rem',
      }}
    >
      {text}
    </Typography>
  </Box>
);

const WeekendView = () => {
  return (
    <AnimatedContainer maxWidth="md">
      <Stack spacing={3.5}>
        <Box
          sx={{
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: '-22px',
              zIndex: 0,
              pointerEvents: 'none',
              borderRadius: '42px',
              background:
                'radial-gradient(circle at 50% 0%, rgba(210,110,255,0.22), rgba(210,110,255,0.08) 36%, rgba(210,110,255,0) 72%)',
              filter: 'blur(34px)',
              animation: `${driftGlow} 8s ease-in-out infinite`,
            },
          }}
        >
          <GlassCard
            sx={{
              position: 'relative',
              zIndex: 1,
              py: { xs: 3.5, sm: 4.5 },
            }}
          >

            <Typography
              sx={{
                fontWeight: 950,
                lineHeight: 0.95,
                letterSpacing: '-0.055em',
                fontSize: { xs: '2.85rem', sm: '4.4rem' },
                color: 'white',
                textWrap: 'balance',
                textShadow:
                  '0 3px 24px rgba(205,120,255,0.18), 0 0 2px rgba(255,255,255,0.22)',
              }}
            >
              The only thing certain in life is change.
            </Typography>

          </GlassCard>
        </Box>

        <GlassCard>
          <Typography
            sx={{
              mb: 0.75,
              fontSize: '0.74rem',
              fontWeight: 900,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'rgba(255,240,255,0.72)',
            }}
          >
            What is The Weakday Show?
          </Typography>

          <Typography
            sx={{
              fontWeight: 950,
              lineHeight: 1,
              letterSpacing: '-0.045em',
              fontSize: { xs: '2.15rem', sm: '3rem' },
              color: 'white',
              textWrap: 'balance',
            }}
          >
            Live comedy with the audience in the driver’s seat.
          </Typography>

          <Typography
            sx={{
              mt: 1.5,
              mx: 'auto',
              maxWidth: '52ch',
              color: 'rgba(255,255,255,0.74)',
              fontWeight: 600,
              lineHeight: 1.48,
              fontSize: { xs: '1rem', sm: '1.08rem' },
            }}
          >
            Vote on each comedian’s set from your phone. Thumbs up if they’re cooking.
            Thumbs down if they’re not.
          </Typography>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            sx={{ mt: 2.5 }}
          >
            <FeaturePill
              icon={<HowToVoteRoundedIcon sx={{ color: 'white', fontSize: 22 }} />}
              title="Vote live"
              text="Your phone becomes part of the show. Every set gets judged in real time."
            />

            <FeaturePill
              icon={<ForumRoundedIcon sx={{ color: 'white', fontSize: 22 }} />}
              title="Send comments"
              text="Audience members can send messages that might hit the crowd screens."
            />

            <FeaturePill
              icon={<CelebrationRoundedIcon sx={{ color: 'white', fontSize: 22 }} />}
              title="Fun, fr"
              text="We'll do anything to make you happy. Yes... Anything."
            />
          </Stack>
        </GlassCard>

        <Box>
          <InstagramAd />
        </Box>
      </Stack>
    </AnimatedContainer>
  );
};

export default WeekendView;